// Paints a ResolvedTheme onto <html>: data-theme for mode, CSS variables for
// every token, and a localStorage cache so index.html can stamp the same
// values before React mounts (no flash). Same trick as the Puzzle Site's
// data-theme attribute, generalized to generated tokens.

import { themeToCssVars, type LookId, type ResolvedTheme } from '@fsp/theme';
import { storageKey } from '../data/storage';

export const THEME_CACHE_KEY = storageKey('theme.resolved.v1');

export interface CachedTheme {
  mode: 'dark' | 'light';
  vars: Record<string, string>;
  /**
   * The look it was resolved on, since a look lays its own ground. First
   * paint skips a cache made on another look (a second tab changed it) rather
   * than stamp Clean's surfaces under Crayon's lines.
   */
  look?: LookId;
}

export function applyTheme(resolved: ResolvedTheme, look: LookId): void {
  const vars = themeToCssVars(resolved);
  paint({ mode: resolved.mode, vars });
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({ mode: resolved.mode, vars, look }));
  } catch {
    // Private mode or storage blocked: the theme still paints, it just won't be cached.
  }
}

export function paint(cached: CachedTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', cached.mode);
  root.style.colorScheme = cached.mode;
  for (const [k, v] of Object.entries(cached.vars)) root.style.setProperty(k, v);
  const meta = document.querySelector('meta[name="theme-color"]');
  const canvas = cached.vars['--fsp-bg-canvas'];
  if (meta && canvas) meta.setAttribute('content', canvas);
}

export function readCachedTheme(): CachedTheme | null {
  try {
    const raw = localStorage.getItem(THEME_CACHE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'mode' in parsed &&
      'vars' in parsed &&
      typeof (parsed as CachedTheme).vars === 'object'
    ) {
      return parsed as CachedTheme;
    }
    return null;
  } catch {
    return null;
  }
}
